import * as React from 'react';
import { CircleCheck, Clock3, Package, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { OrderKpiMetrics } from '@klareospace/pharos-lib-types';
import { ErrorBanner, KpiCard, KpiCardSkeleton } from '@klareospace/pharos-lib-web-utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const SKELETON_CARD_COUNT = 4;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderKpisMonthProps {
  isError: boolean;
  isLoading: boolean;
  metrics: OrderKpiMetrics | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Displays the month-to-date `order-kpis` as 4 `KpiCard`s (Total, On time, Late, Failed) — the other 4 `OrderKpiMetrics` counters aren't shown here. */
export function OrderKpisMonth({ isError, isLoading, metrics }: OrderKpisMonthProps): React.JSX.Element {
  const { t } = useTranslation('kpis');

  if (isError) return <ErrorBanner message={t('loadError')} />;

  if (isLoading || !metrics) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiCard
        icon={<Package size={18} />}
        label={t('month.metrics.total')}
        tooltip={t('month.tooltips.total')}
        value={metrics.total}
        variant="neutral"
      />
      <KpiCard
        icon={<CircleCheck size={18} />}
        label={t('month.metrics.completedOnTime')}
        tooltip={t('month.tooltips.completedOnTime')}
        value={metrics.completedOnTime}
        valueAccent="success"
        variant="success"
      />
      <KpiCard
        icon={<Clock3 size={18} />}
        label={t('month.metrics.completedLate')}
        tooltip={t('month.tooltips.completedLate')}
        value={metrics.completedLate}
        variant="warning"
      />
      <KpiCard
        icon={<TriangleAlert size={18} />}
        label={t('common.failed')}
        tooltip={t('month.tooltips.failed')}
        value={metrics.failed}
        valueAccent="destructive"
        variant="danger"
      />
    </div>
  );
}
